import { Box, Chip, IconButton, Tooltip } from '@mui/material';
import { HttpStatusCode } from 'axios';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { MdOutlineScheduleSend } from "react-icons/md";
import { SnackbarContext } from '../../contexts/Snackbar';
import { RESEND_VERIFICATION_EMAIL } from '../../enums/ApiPaths';
import useConfirm from '../../hooks/useConfirm';
import useUser from '../../hooks/useUser';
import authApi from '../../utils/api/authApi';

const verificationObj = {
    verified: {
        color: "success",
        tooltip: "UserVerificationSuccessEmailTooltipMessage",
        text: "Verified",
    },
    pending: {
        color: "warning",
        tooltip: "UserVerificationPendingEmailTooltipMessage",
        text: "Pending",
        confirmationMsg: "UserHasAVerificationPending"
    },
    failed: {
        color: "error",
        tooltip: "UserVerificationFailedEmailTooltipMessage",
        text: "Failed",
        confirmationMsg: "UserHasAVerificationError"
    }
}

export default function UserVerification({ data }) {
    const hasEmail = data?.userDetails?.email;
    const verification = data?.verification;
    const lastVerificationMsgTime = data?.lastVerificationTokenSendTime;
    const { t } = useTranslation();
    const { roles } = useUser();
    const { handleSnackbar } = React.useContext(SnackbarContext);

      const {
        ConfirmationDialog,
        confirm,
        getMessage,
    } = useConfirm(t("VerificationMsgLastDateSent"), t("Accept"), t("Cancel"));

    if (!verification || !roles.master) return null;
    
    const statusKey = String(verification).toLowerCase();
    const status = verificationObj[statusKey] || verificationObj.pending;
    const needsAction = ["failed", "pending"].includes(statusKey) && hasEmail;

    async function resendEmail() {
        const confirmationMessage = t(status.confirmationMsg);
        const msg = lastVerificationMsgTime ? 
                    confirmationMessage + " " + t("VerificationMsgLastDateSent") + " " + lastVerificationMsgTime : 
                    confirmationMessage;

        getMessage(msg);
        const ans = await confirm();

        if (!ans) return;

        try {
        const res = await authApi.post(
            `${RESEND_VERIFICATION_EMAIL}?email=${data?.userDetails?.email}`
        );
        if (res.status === HttpStatusCode.Ok) {
            handleSnackbar("VerificationResend", "success");
        }
        } catch (error) {
            handleSnackbar(error?.response?.data, "error");
        }
    }

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <StatusChip status={status} />
            
            {needsAction && (
                <IconButton 
                        title={t("ResendVerification")}
                        variant="contained"
                        onClick={resendEmail}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 500,
                        }}
                    >
                        <MdOutlineScheduleSend/>
                </IconButton>
            )}
            <ConfirmationDialog/>
        </Box>
    );
}

function StatusChip({ status }) {
    const { t } = useTranslation();
    
    return (
        <Tooltip title={t(status.tooltip)}>
            <Chip
                label={t(status.text)}
                color={status.color}
                sx={{
                    fontWeight: 600,
                    '& .MuiChip-icon': {
                        color: 'inherit',
                        ml: 0.5
                    }
                }}
            />
        </Tooltip>
    );
}